import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuthStore } from '../store/authStore';
import { useModuleStore, ERP_MODULES } from '../store/moduleStore';
import { useThemeStore } from '../store/themeStore';
import { useTenantStore } from '../store/tenantStore';
import { usePermissionStore } from '../store/permissionStore';
import { useCompanyPath, useCompanySlug, stripCompanyPrefix, inferModuleFromPath } from '../utils/routing';

const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { activeModuleId, setModule } = useModuleStore();
  const { isDark, toggle: toggleTheme } = useThemeStore();
  const { branchId, branchName } = useTenantStore();
  const location = useLocation();
  const navigate = useNavigate();
  const slug = useCompanySlug();
  const loginPath = useCompanyPath('/login');
  const modulesPath = useCompanyPath('/modules');
  const locationPath = useCompanyPath('/location');

  const activeModule = ERP_MODULES.find(m => m.id === activeModuleId);

  // Load permissions once authenticated (sidebar + module filtering)
  useEffect(() => {
    if (user) usePermissionStore.getState().load();
  }, [user?.id]);

  // Require branch selection (or explicit "All Branches" for admins) before module pages
  useEffect(() => {
    if (branchId === null && !branchName) {
      navigate(locationPath, { replace: true });
    }
  }, [branchId, branchName, locationPath, navigate]);

  // Keep sidebar module in sync when user lands on a deep link
  useEffect(() => {
    const rel = stripCompanyPrefix(location.pathname, slug);
    const mod = inferModuleFromPath(rel);
    if (mod && mod !== activeModuleId) setModule(mod);
  }, [location.pathname, slug, activeModuleId, setModule]);
  // never leave a collapsed rail on phones, and close the drawer on desktop.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setCollapsed(false);
      else setMobileOpen(false);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock background scroll while the mobile drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const toggleSidebar = () => {
    if (window.innerWidth < 768) setMobileOpen(o => !o);
    else setCollapsed(c => !c);
  };
  const closeMobile = () => setMobileOpen(false);

  const handleLogout = () => {
    logout();
    setModule(null);
    usePermissionStore.getState().reset();
    navigate(loginPath);
  };
  const goModules = () => { setMobileOpen(false); navigate(modulesPath); };

  return (
    <div className="flex h-screen bg-surface overflow-hidden">
      <Sidebar collapsed={collapsed} mobileOpen={mobileOpen} onNavigate={closeMobile} />

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={closeMobile} aria-hidden="true" />
      )}

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ml-0 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        <header className="bg-surface-card border-b border-border px-3 sm:px-4 py-2.5 flex items-center justify-between shadow-card flex-shrink-0 z-20">
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleSidebar}
              className="p-2 rounded-xl hover:bg-surface-muted text-content-muted hover:text-primary-600 transition-all"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <svg className="w-5 h-5 md:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg className="w-5 h-5 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {collapsed
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
                }
              </svg>
            </button>

            <div className="flex items-center gap-2 text-sm hidden sm:flex">
              <button onClick={goModules} className="text-content-muted hover:text-primary-600 transition-colors font-medium flex items-center gap-1">
                <span className="text-base">⊞</span>
                <span>Modules</span>
              </button>
              {activeModule && (
                <>
                  <svg className="w-3 h-3 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gradient-to-r ${activeModule.color} text-white text-xs font-semibold shadow-sm`}>
                    <span>{activeModule.icon}</span>
                    <span>{activeModule.name}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 bg-surface-muted border border-border rounded-xl px-3 py-2 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
              <svg className="w-4 h-4 text-content-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input placeholder="Search..." className="bg-transparent text-sm outline-none text-content-secondary w-full md:w-32" />
            </div>

            <button onClick={() => navigate(locationPath)}
              className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-surface-muted border border-border text-xs text-content-secondary hover:text-primary-600 transition-colors"
              title="Change location">
              <span>📍</span>
              <span className="max-w-[120px] truncate">{branchName || 'Select location'}</span>
            </button>

            <button onClick={toggleTheme}
              className="p-2 rounded-xl hover:bg-surface-muted transition-colors"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <span className="text-lg">{isDark ? '☀️' : '🌙'}</span>
            </button>

            <button className="p-2 rounded-xl hover:bg-surface-muted relative transition-colors">
              <svg className="w-5 h-5 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-slate-800" />
            </button>

            <button onClick={handleLogout}
              className="flex items-center gap-2.5 hover:bg-surface-muted rounded-xl px-3 py-2 transition-colors"
              title="Logout">
              <div className="w-8 h-8 gradient-bg rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-semibold text-content leading-tight">{user?.full_name || 'Admin'}</div>
                <div className="text-xs text-content-muted">Logout</div>
              </div>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-surface">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
